'use client';

import { localeOptions } from '@i18n/locales';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef } from 'react';
import type { InboxIconType, InboxNotification } from '@/lib/api/notifications/inbox-client';
import {
  useMarkInboxNotificationRead,
  useMuteComment,
  useMuteSource,
} from '@/lib/api/notifications/inbox-hooks';
import { scrollToComment } from '@/lib/components/Comments/scrollToComment';
import { Link, usePathname } from '@/lib/routing';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/lib/ui-kit/dropdown-menu';
import { Icon, type IconName } from '@/lib/ui-kit/icons';
import { Skeleton } from '@/lib/ui-kit/skeleton';
import { cn } from '@/lib/ui-kit/utils';
import { getZoneFromHref, getZoneFromPath } from '@/lib/utils/zones';

// Семантический фолбэк, когда бэкенд не прислал iconUrl (см. контракт
// InboxNotification.iconType) — инлайн-иконки из барреля, без сети. Тон один
// для всех типов: смысл несёт форма иконки, а не цвет.
const ICON_TYPE_FALLBACK: Record<InboxIconType, IconName> = {
  success: 'CheckCircle',
  info: 'Info',
  warn: 'Warning',
  warning: 'Warning',
  alert: 'Attention',
  error: 'Attention',
  failed: 'CloseCircle',
  neutral: 'Info',
};

/**
 * Returns a map of `id -> position in the fresh batch` (0-based, top-down) for
 * items that are new compared to the previous render. Position drives the
 * cascade `animation-delay` so a burst of arrivals plays out as a wave instead
 * of one synchronous flash.
 *
 * First non-empty render seeds the "seen" set silently — only items that
 * arrive AFTER that (e.g. WS `INBOX_NEW` patching the cache) are reported as
 * fresh, so the initial inbox load doesn't trigger 20 simultaneous slide-ins.
 *
 * `resetKey` clears the seen set when the dataset identity changes (category
 * switch, pagination). The next render reseeds silently so those items don't
 * animate — only genuine new arrivals do.
 */
export function useFreshItemIds<T extends { id: string }>(
  items: T[],
  resetKey?: unknown,
): Map<string, number> {
  const prevIdsRef = useRef<Set<string> | null>(null);
  const prevResetKeyRef = useRef(resetKey);

  if (prevResetKeyRef.current !== resetKey) {
    prevIdsRef.current = null;
    prevResetKeyRef.current = resetKey;
  }

  const freshMap = useMemo(() => {
    const map = new Map<string, number>();
    if (prevIdsRef.current === null) return map;
    let staggerIdx = 0;
    for (const item of items) {
      if (!prevIdsRef.current.has(item.id)) {
        map.set(item.id, staggerIdx);
        staggerIdx += 1;
      }
    }
    return map;
  }, [items]);

  useEffect(() => {
    if (prevIdsRef.current === null && items.length === 0) return;
    prevIdsRef.current = new Set(items.map((it) => it.id));
  });

  return freshMap;
}

/**
 * Compact relative time, e.g. "5s ago", "2m ago", "3h ago".
 *
 * Diff is taken from epoch (UTC) so timezone drift never affects the copy.
 * Anything under 5s collapses to `justNowLabel` (default "just now") instead
 * of "0s ago".
 */
export function formatNarrowRelative(
  iso: string,
  locale: string,
  justNowLabel = 'just now',
): string {
  const deltaSec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (deltaSec < 5) return justNowLabel;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always', style: 'narrow' });
  if (deltaSec < 60) return rtf.format(-deltaSec, 'second');
  const deltaMin = Math.round(deltaSec / 60);
  if (deltaMin < 60) return rtf.format(-deltaMin, 'minute');
  const deltaHour = Math.round(deltaMin / 60);
  if (deltaHour < 24) return rtf.format(-deltaHour, 'hour');
  const deltaDay = Math.round(deltaHour / 24);
  if (deltaDay < 30) return rtf.format(-deltaDay, 'day');
  const deltaMonth = Math.round(deltaDay / 30);
  if (deltaMonth < 12) return rtf.format(-deltaMonth, 'month');
  return rtf.format(-Math.round(deltaMonth / 12), 'year');
}

const STAGGER_STEP_MS = 90;

// Backend notification type for "new login on a different device". Clicking it
// pops the Account Activities modal instead of navigating to a route.
const ACCOUNT_ACTIVITY_NOTIFICATION_TYPE = 'NEW_LOGIN';

interface NotificationItemProps {
  item: InboxNotification;
  locale: string;
  /** Side-effect on click (e.g. close drawer). Navigation to /notifications happens regardless. */
  onClick?: (item: InboxNotification) => void;
  /**
   * Invoked for account-activity notifications (e.g. new login on a different
   * device) instead of navigating — opens the Account Activities modal.
   */
  onOpenAccountActivities?: () => void;
  /**
   * Zero-based position within the fresh batch; presence triggers the
   * slide+fade animation, and the value drives the cascade delay.
   */
  freshIndex?: number;
}

export function NotificationItemSkeleton() {
  // ui-kit Skeleton uses `bg-v2-surface-secondary` which is pure white on the
  // light theme. Override with `bg-v2-btn-secondary` so the shimmer is visible
  // on both themes (#171a1b dark / #f1f1f1 light).
  const skeletonBg = '!bg-v2-btn-secondary';
  return (
    <li className="border-v2-border border-b last:border-b-0">
      <div className="-mx-3 flex items-start gap-3 px-3 pt-2 pb-4 max-sm:pt-3">
        <div className="flex shrink-0 items-start gap-[2px] pt-[3px]">
          <span aria-hidden className="size-[5px] rounded-full bg-transparent" />
          <Skeleton className={`size-5 rounded-full ${skeletonBg} max-sm:hidden`} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <Skeleton className={`h-4 w-[60%] rounded-md ${skeletonBg}`} />
            <Skeleton className={`h-3 w-[44px] shrink-0 rounded-md ${skeletonBg}`} />
          </div>
          <Skeleton className={`h-3 w-[85%] rounded-md ${skeletonBg}`} />
        </div>
      </div>
    </li>
  );
}

const ROW_CLASSNAME =
  '-mx-3 flex items-start gap-3 rounded-[10px] px-3 pt-2 pb-4 text-left transition-colors hover:bg-v2-btn-secondary max-sm:pt-3';

function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

// Build once: matches a leading "/<locale>" segment for any supported locale.
const LOCALE_PREFIX_RE = new RegExp(
  `^/(?:${localeOptions.map((l) => l.code.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')).join('|')})(?=/|$)`,
);

/**
 * Backend embeds the user's UI locale in ctaUrl (e.g. "/en/account?..."), but
 * our locale-aware `Link` from `@/lib/routing` adds the prefix on its own. So
 * strip any known leading locale segment to avoid `/en/en/account?...`.
 * Unknown prefixes are left intact.
 */
function resolveInternalHref(rawHref: string): string {
  return rawHref.replace(LOCALE_PREFIX_RE, '') || '/';
}

/**
 * notification-service emits `/{locale}/futures?symbol=BTCUSDT` for FUTURES rows,
 * but the trade zone is mounted at `/trade`. Only the path segment is rewritten, so
 * the row opens the terminal instead of 404ing; the query is ignored, meaning the
 * terminal lands on the persisted pair, not necessarily the notification's. Exact
 * deep links need the emitter fixed to `/trade/{quote}/{symbol}` — mapping the
 * symbol to a quote segment here would mean hardcoding quote currencies in the
 * web zone, which has no pairs list to resolve them against.
 */
function rewriteFuturesHref(href: string): string {
  return href.replace(/\/futures(?=\?|#|$)/, '/trade');
}

export function NotificationItem({
  item,
  locale,
  onClick,
  onOpenAccountActivities,
  freshIndex,
}: NotificationItemProps) {
  const t = useTranslations('notifications');
  const tActions = useTranslations('notifications.actions');
  const pathname = usePathname();
  const isFresh = freshIndex !== undefined;
  const markRead = useMarkInboxNotificationRead();
  const muteComment = useMuteComment();
  const muteSource = useMuteSource();
  const isCommentAnchor = item.category === 'COMMENTS' && typeof item.commentId === 'number';
  const handleClick = () => {
    if (!item.read) {
      markRead.mutate({ id: item.id, category: item.category });
    }
    // Same-page: comment is already mounted, so center it here (Link scroll is
    // off). Cross-page: CommentsList handles it after the thread loads. Delay
    // lets the drawer close and release its scroll lock.
    if (isCommentAnchor) {
      setTimeout(() => scrollToComment(item.commentId!), 150);
    }
    onClick?.(item);
  };

  // New-login notifications open the Account Activities modal in place rather
  // than navigating, so the user can review the session immediately.
  const opensAccountActivities =
    item.type === ACCOUNT_ACTIVITY_NOTIFICATION_TYPE &&
    typeof onOpenAccountActivities === 'function';

  const handleActivitiesClick = () => {
    handleClick();
    onOpenAccountActivities?.();
  };

  // Only COMMENTS rows that carry a backend commentId can be muted per-thread.
  const canMuteThread = isCommentAnchor;
  // Comment rows enriched with sourceType+sourceId can mute the whole market/news.
  const canMuteSource =
    item.category === 'COMMENTS' &&
    (item.sourceType === 'MARKET' || item.sourceType === 'NEWS_ARTICLE') &&
    typeof item.sourceId === 'string' &&
    item.sourceId.length > 0;
  const showMenu = canMuteThread || canMuteSource;

  const sourceMuteLabel =
    item.sourceType === 'NEWS_ARTICLE' ? tActions('muteNewsArticle') : tActions('muteMarket');

  const itemCls =
    'cursor-pointer rounded-[8px] px-3 py-2 text-[13px] text-v2-text-primary leading-[16px] tracking-[-0.13px] focus:bg-v2-btn-secondary';

  const menu = showMenu ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={tActions('menu')}
          // Stop the link/handler chain — opening the menu must not navigate.
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          className="-ml-1 inline-flex size-9 shrink-0 items-center justify-center rounded-full text-v2-text-secondary outline-none transition-colors hover:bg-v2-btn-secondary hover:text-v2-text-primary focus-visible:ring-2 focus-visible:ring-v2-border"
        >
          <Icon name="EllipsisVertical" size={20} className="rotate-90" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={4}
        className="min-w-[200px] border-v2-border bg-v2-surface-secondary p-1"
        onClick={(e) => e.stopPropagation()}
      >
        {canMuteThread && (
          <DropdownMenuItem
            disabled={muteComment.isPending}
            onSelect={() => {
              if (typeof item.commentId === 'number') {
                muteComment.mutate({ commentId: item.commentId });
              }
            }}
            className={itemCls}
          >
            {tActions('muteDiscussion')}
          </DropdownMenuItem>
        )}
        {canMuteSource && (
          <DropdownMenuItem
            disabled={muteSource.isPending}
            onSelect={() => {
              if (
                (item.sourceType === 'MARKET' || item.sourceType === 'NEWS_ARTICLE') &&
                typeof item.sourceId === 'string' &&
                item.sourceId.length > 0
              ) {
                muteSource.mutate({ sourceType: item.sourceType, sourceId: item.sourceId });
              }
            }}
            className={itemCls}
          >
            {sourceMuteLabel}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  // Unknown iconType (backend added a new one) → no icon rather than a crash.
  const iconFallback = item.iconType ? ICON_TYPE_FALLBACK[item.iconType] : undefined;

  const rowContent = (
    <>
      <div className="flex shrink-0 items-start gap-[2px] pt-[3px]">
        <span
          aria-hidden
          className={cn('size-[5px] rounded-full', item.read ? 'bg-transparent' : 'bg-v2-bg-blue')}
        />
        <span
          aria-hidden
          className={cn(
            'inline-flex size-5 items-center justify-center text-[16px] leading-none',
            // Mockup has no icon column on mobile — collapse the empty slot there
            // (desktop keeps it) so titles sit tight to the unread dot.
            !item.iconUrl && !iconFallback && 'max-sm:hidden',
          )}
        >
          {item.iconUrl ? (
            // biome-ignore lint/performance/noImgElement: small CDN-hosted SVG icon, next/image needs per-host allowlist and gains nothing on inline SVGs.
            <img src={item.iconUrl} alt="" width={20} height={20} className="size-5" />
          ) : iconFallback ? (
            <Icon name={iconFallback} size={20} className="text-v2-text-secondary" />
          ) : null}
        </span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-baseline justify-between gap-3">
          <p className="line-clamp-2 min-w-0 flex-1 font-normal text-[15px] text-v2-text-primary leading-[26px]">
            {item.title}
          </p>
          {/* Menu comes BEFORE the timestamp: the button renders only on muteable
              rows, so keeping the time as the last (rightmost) element pins it to
              the row edge whether or not the menu exists. */}
          <div className="flex shrink-0 items-center gap-1">
            {menu}
            <p className="whitespace-nowrap text-[12px] text-v2-text-secondary leading-[1.15]">
              {formatNarrowRelative(item.createdAt, locale, t('justNow'))}
            </p>
          </div>
        </div>
        {item.quote ? (
          <p className="line-clamp-1 w-full text-[13px] text-v2-text-secondary italic leading-[16px] tracking-[-0.13px]">
            {t('yourComment', { quote: item.quote })}
          </p>
        ) : null}
        {item.body
          ? // `\n` in body is an intended hard line break (e.g. BET_WON position line,
            // withdraw address). Per-segment clamp keeps the detail line visible even
            // when the first line wraps — a shared 2-line clamp would clip it.
            item.body.split('\n').map((line, i) => (
              <p
                // Body segments are positional (title, then detail); index key is stable
                // because the backend never reorders them within a single body string.
                key={i}
                className="line-clamp-2 w-full text-[13px] text-v2-text-primary leading-[16px] tracking-[-0.13px] opacity-50"
              >
                {line}
              </p>
            ))
          : null}
      </div>
    </>
  );

  // Navigation target: explicit ctaUrl from backend, otherwise fall back to
  // the notification center so the click is never a dead end.
  let rawHref = item.ctaUrl ? rewriteFuturesHref(item.ctaUrl) : '/notifications';
  // Old BET_WON rows (written before notification-service v1.64) carry a bare
  // transactions URL — append the tab param so they still open the Prediction
  // tab. Their txId is not recoverable, so they open without highlight.
  if (item.type === 'BET_WON' && !rawHref.includes('txTab=')) {
    rawHref += `${rawHref.includes('?') ? '&' : '?'}txTab=Prediction`;
  }
  // Deep-link to the comment. Strip any existing fragment first so a ctaUrl the
  // backend already anchored can't produce `#comment-19#comment-19`.
  const href = isCommentAnchor ? `${rawHref.split('#')[0]}#comment-${item.commentId}` : rawHref;
  // Multi-zone: soft <Link> nav only resolves inside the current zone, so a row
  // pointing at another app (futures terminal, news, auth) must be a plain <a>
  // with an explicit locale prefix.
  const isCrossZone = getZoneFromHref(href) !== getZoneFromPath(pathname);
  const row = opensAccountActivities ? (
    <button
      type="button"
      onClick={handleActivitiesClick}
      // Buttons shrink-to-fit, so the width must be set explicitly — and plain
      // w-full is 24px short: ROW_CLASSNAME's -mx-3 stretches anchor rows to
      // 100% + 1.5rem, and the button must match or its timestamp lands 24px
      // left of the other rows'.
      className={cn(ROW_CLASSNAME, 'w-[calc(100%+1.5rem)]')}
    >
      {rowContent}
    </button>
  ) : isExternalHref(href) ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className={ROW_CLASSNAME}
    >
      {rowContent}
    </a>
  ) : isCrossZone ? (
    <a
      href={`/${locale}${resolveInternalHref(href)}`}
      onClick={handleClick}
      className={ROW_CLASSNAME}
    >
      {rowContent}
    </a>
  ) : (
    <Link
      href={resolveInternalHref(href)}
      scroll={!isCommentAnchor}
      onClick={handleClick}
      className={ROW_CLASSNAME}
    >
      {rowContent}
    </Link>
  );

  return (
    <li
      className={cn(
        'border-v2-border border-b last:border-b-0',
        isFresh && 'fade-in slide-in-from-left-4 animate-in duration-300',
      )}
      style={
        isFresh
          ? {
              animationDelay: `${freshIndex * STAGGER_STEP_MS}ms`,
              // `backwards` keeps the item at its starting (offscreen+invisible)
              // state during the staggered delay so each row's slide-in begins
              // from the same off-screen position.
              animationFillMode: 'backwards',
            }
          : undefined
      }
    >
      {row}
    </li>
  );
}
